import { ILoginPassword } from '@gen2/api/login/api';
import { useLoginPasswordMutation } from '@gen2/api/login/hooks';
import { useAuth } from '@gen2/hooks';
import { ErrorResponse } from '@gen2/utils/formatMessage';
import { yupResolver } from '@hookform/resolvers/yup';
import LoadingButton from '@mui/lab/LoadingButton';
import { Alert, Checkbox, FormControlLabel, TextField } from '@mui/material';
import { AxiosResponse } from 'axios';
import { has } from 'lodash';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import * as Yup from 'yup';
import { EmailIdentifier, Form, Hint } from './Login.styled';
import TurnstileWidget from './TurnstileWidget';

interface PasswordProps {
  active: boolean;
  email: string;
  handleBack: (step?: number) => void;
  handleMfaRequired: () => void;
  focus?: boolean;
}

const schema = Yup.object().shape({
  password: Yup.string().required('password.required'),
});

const PasswordForm: FC<PasswordProps> = ({
  active,
  email,
  handleBack,
  handleMfaRequired,
  focus,
}) => {
  const { t } = useTranslation('validation');

  const [checked, setChecked] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isShow, setIsShow] = useState(false);
  const [messages, setMessages] = useState<string[]>([]);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileKey, setTurnstileKey] = useState(0);
  const [rateLimitRemaining, setRateLimitRemaining] = useState<number | null>(
    null,
  );
  const [hasTurnstileRequiredMessage, setHasTurnstileRequiredMessage] =
    useState(false);
  const resetTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { mutate } = useLoginPasswordMutation();
  const { authCode } = useAuth();

  // Cleanup function to clear any pending timeouts
  const clearResetTimeout = useCallback(() => {
    if (resetTimeoutRef.current) {
      clearTimeout(resetTimeoutRef.current);
      resetTimeoutRef.current = null;
    }
  }, []);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<ILoginPassword>({
    defaultValues: {
      password: '',
    },
    resolver: yupResolver(schema),
  });

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setChecked(event.target.checked);
  };

  const extractRateLimitRemaining = useCallback(
    (headers: Record<string, unknown> | undefined): number | null => {
      if (!headers) {
        return null;
      }

      let headerValue: unknown = null;

      if (headers && typeof (headers as { get?: unknown }).get === 'function') {
        const headersWithGet = headers as { get: (key: string) => unknown };
        headerValue =
          headersWithGet.get('x-ratelimit-remaining') ||
          headersWithGet.get('X-RateLimit-Remaining');
      } else {
        headerValue =
          headers['x-ratelimit-remaining'] ||
          headers['X-RateLimit-Remaining'] ||
          null;
      }

      if (headerValue === null || headerValue === undefined) {
        return null;
      }

      const remaining = parseInt(String(headerValue).trim(), 10);
      return isNaN(remaining) ? null : remaining;
    },
    [],
  );

  const requiresTurnstile = useMemo(
    () => rateLimitRemaining !== null && rateLimitRemaining <= 1,
    [rateLimitRemaining],
  );

  const handleRateLimitHeaders = useCallback(
    (headers: Record<string, unknown> | undefined) => {
      if (!headers) {
        return;
      }

      const remaining = extractRateLimitRemaining(headers);
      if (remaining !== null) {
        setRateLimitRemaining(remaining);
      }
    },
    [extractRateLimitRemaining],
  );

  const handleUnauthorizedError = useCallback((data: unknown) => {
    const errorData = data as { message?: string | string[] };
    if (errorData?.message) {
      const message = errorData.message;
      setMessages(Array.isArray(message) ? message : [message]);
    }
  }, []);

  const handleValidationError = useCallback(() => {
    setMessages([t('email.invalid')]);
  }, [t]);

  const handleBadRequestError = useCallback(
    (data: unknown) => {
      const errorData = data as
        | { message?: string; error?: string }
        | string
        | null;
      const errorMessage =
        (typeof errorData === 'object' && errorData?.message) ||
        (typeof errorData === 'object' && errorData?.error) ||
        (typeof errorData === 'string' ? errorData : null) ||
        t('error.400', { ns: 'common' });
      setMessages([errorMessage as string]);
    },
    [t],
  );

  const handleRateLimitError = useCallback((data: unknown) => {
    const errorData = data as { message?: string | string[] };
    if (errorData?.message) {
      const message = errorData.message;
      setMessages(Array.isArray(message) ? message : [message]);
    }
  }, []);

  const handleServerError = useCallback(() => {
    setMessages([t('error.500', { ns: 'common' })]);
  }, [t]);

  const handleErrorStatus = useCallback(
    (status: number, data: unknown) => {
      if (status === 401) {
        handleUnauthorizedError(data);
      } else if (status === 422) {
        const { errors } = data as ErrorResponse;
        if (errors && has(errors, 'email')) {
          handleValidationError();
        }
      } else if (status === 400) {
        handleBadRequestError(data);
      } else if (status === 429) {
        handleRateLimitError(data);
      } else if (status >= 500) {
        handleServerError();
      }
    },
    [
      handleUnauthorizedError,
      handleValidationError,
      handleBadRequestError,
      handleRateLimitError,
      handleServerError,
    ],
  );

  const onSubmit: SubmitHandler<ILoginPassword> = (form) => {
    if (requiresTurnstile && !turnstileToken) {
      setIsShow(true);
      setMessages([
        t('turnstile.required', {
          ns: 'validation',
          defaultValue: 'Please complete the security verification',
        }),
      ]);
      setHasTurnstileRequiredMessage(true);

      return;
    }

    const tokenToUse = turnstileToken;

    if (requiresTurnstile) {
      setTurnstileToken(null);
    }

    const newForm = {
      ...form,
      email,
      ...(tokenToUse && { captcha_token: tokenToUse }),
    };

    setIsLoading(true);

    if (requiresTurnstile && tokenToUse) {
      clearResetTimeout();

      resetTimeoutRef.current = setTimeout(() => {
        setTurnstileKey((prev) => prev + 1);
        resetTimeoutRef.current = null;
      }, 100);
    }

    mutate(newForm, {
      onError: (error: unknown) => {
        const axiosError = error as AxiosResponse;

        if (!axiosError) {
          clearResetTimeout();
          setIsLoading(false);
          return;
        }

        const { status, data, headers } = axiosError;
        setIsShow(true);

        handleRateLimitHeaders(headers as Record<string, unknown> | undefined);
        handleErrorStatus(status, data);

        clearResetTimeout();
        setIsLoading(false);
      },
      onSuccess: (res: unknown) => {
        const response = res as AxiosResponse;

        const remaining = extractRateLimitRemaining(
          response.headers as Record<string, unknown>,
        );

        if (remaining !== null) {
          setRateLimitRemaining(remaining);
        }

        setTurnstileToken(null);

        if (response.status === 202) {
          setIsLoading(false);
          handleMfaRequired();
        } else {
          authCode();
        }

        clearResetTimeout();
      },
    });
  };

  const handleTurnstileSuccess = useCallback(
    (token: string) => {
      setTurnstileToken((currentToken) => {
        if (!currentToken) {
          return token;
        }
        return currentToken;
      });

      if (hasTurnstileRequiredMessage) {
        setIsShow(false);
        setMessages([]);
        setHasTurnstileRequiredMessage(false);
      }
    },
    [hasTurnstileRequiredMessage],
  );

  const handleTurnstileError = useCallback(() => {
    setTurnstileToken(null);
    setIsShow(true);
    setMessages([
      t('turnstile.error', {
        ns: 'validation',
        defaultValue: 'Security verification failed. Please try again.',
      }),
    ]);
  }, [t]);

  const handleTurnstileExpire = useCallback(() => {
    setTurnstileToken(null);
    setIsShow(true);
    setMessages([
      t('turnstile.expired', {
        ns: 'validation',
        defaultValue: 'Security verification expired. Please try again.',
      }),
    ]);
  }, [t]);

  useEffect(() => {
    if (!active) {
      setIsShow(false);
      setMessages([]);
      setTurnstileToken(null);
      setRateLimitRemaining(null);
      setHasTurnstileRequiredMessage(false);
      setTurnstileKey((prev) => prev + 1);
      clearResetTimeout();
    }

    return () => {
      clearResetTimeout();
    };
  }, [active, clearResetTimeout]);

  if (!active || !email) return null;

  return (
    <Form>
      {isShow && (
        <Alert data-cy="alert" icon={false} severity="error" color="error">
          {messages}
        </Alert>
      )}
      <EmailIdentifier data-cy="email-identifier" onClick={() => handleBack(1)}>
        <span>{email}</span>
      </EmailIdentifier>
      <>
        <Controller
          name="password"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              id="password"
              fullWidth
              required
              autoFocus={!!focus}
              label={t('password.label')}
              error={Boolean(errors.password)}
              type={checked ? 'text' : 'password'}
              helperText={
                errors.password?.message && t(errors.password.message)
              }
              inputProps={{
                'data-cy': 'password-input',
              }}
            />
          )}
        />
        <FormControlLabel
          control={
            <Checkbox
              color="secondary"
              checked={checked}
              onChange={handleChange}
              inputProps={
                {
                  'data-cy': 'checkbox',
                } as object
              }
            />
          }
          label={t('login.checkbox_label', { ns: 'page' })}
        />
      </>
      <Hint data-cy="link">
        <Link to="/forgot-password">
          {' '}
          {t('login.forgot_password', { ns: 'page' })}
        </Link>
      </Hint>
      {requiresTurnstile && (
        <TurnstileWidget
          key={turnstileKey}
          onSuccess={handleTurnstileSuccess}
          onError={handleTurnstileError}
          onExpire={handleTurnstileExpire}
          theme="auto"
          size="normal"
        />
      )}
      <LoadingButton
        variant="contained"
        color="primary"
        loading={isLoading}
        type="submit"
        onClick={handleSubmit(onSubmit)}
        disabled={requiresTurnstile && !turnstileToken && !isLoading}
        data-cy="next"
      >
        {t('next', { ns: 'common' })}
      </LoadingButton>
    </Form>
  );
};
export default PasswordForm;
